/**
 * Contrato de Outros Bancos, das importações, da calculadora, da recorrência e
 * dos documentos de Finanças.
 *
 * Papel: aqui ficam os corpos e as respostas de `financas-folha`,
 * `financas-outros-bancos` e `financas-documentos` — a configuração da casa, o
 * mapa CPF→nome, as importações da folha e da guia DAE, a folha XLSX, a
 * Calculadora Petrella, a recorrência, a situação do mês de cada favorecido de
 * conta apartada, as folhas fechadas, os lançamentos de outros bancos, o recibo
 * e a ordem de pagamento.
 *
 * Como usar: dinheiro é `dinheiroSchema`, data é `dataSchema` (ISO) e
 * competência é `competenciaSchema` (`AAAA-MM`); nada trafega em formato
 * brasileiro. Os totais da folha importada, da folha fechada e da guia **não são
 * campo de entrada**: a API os soma na resposta, e um total mandado no corpo é
 * descartado pelo parse.
 *
 * A calculadora manda os cartões, e não os itens: a API recalcula os itens por
 * `itensDaCalculadora`, e um valor calculado que viesse no corpo seria ignorado.
 */
import { z } from 'zod';

import { dataSchema, idSchema, textoObrigatorio, textoOpcional } from './comum';
import { cpfDoGrupoSchema, formaPagamentoLancamentoSchema } from './financas';
import { competenciaSchema, dinheiroSchema, valorPorUnidadeOpcionalSchema } from './formatos';

/** Dinheiro maior que zero, como toda linha que vira lançamento ou item. */
const valorPositivoSchema = dinheiroSchema.refine((valor) => valor > 0, {
  message: 'O valor precisa ser maior que zero.',
});

/** Dinheiro já gravado, como a resposta o traz. */
const valorGravadoSchema = z.number();

/** Os dias de um cartão, inteiros de 0 a 31, ou nulos quando não gravados. */
const diaDoMesSchema = z
  .number()
  .int({ message: 'Os dias precisam ser um número inteiro.' })
  .min(0, { message: 'Os dias vão de 0 a 31.' })
  .max(31, { message: 'Os dias vão de 0 a 31.' })
  .nullable();

/* ------------------------------------------------------------- configuração */

/**
 * A resposta de `GET /financas/configuracao`: os textos da casa que a origem
 * escrevia no HTML. Sem linha gravada, os três textos vêm vazios.
 */
export const configuracaoFinanceiraSchema = z.object({
  pagadorPadrao: z.string(),
  siglaDoPagador: z.string(),
  siglaDaFolhaSemColaborador: z.string(),
  recorrenciaVerificadaEm: dataSchema.nullable(),
});

export type ConfiguracaoFinanceira = z.infer<typeof configuracaoFinanceiraSchema>;

/** Um par do mapa CPF→nome que os parsers e a calculadora consultam. */
export const nomeDaFolhaSchema = z.object({
  cpf: z.string().regex(/^\d{11}$/, { message: 'CPF inválido.' }),
  nome: z.string(),
});

export type NomeDaFolha = z.infer<typeof nomeDaFolhaSchema>;

/** A resposta de `GET /financas/folha/nomes-por-cpf`. */
export const nomesDaFolhaSchema = z.array(nomeDaFolhaSchema);

/** O recorte das rotas que leem um mês de uma casa. */
export const consultaDoMesSchema = z.object({
  residenciaId: idSchema,
  competencia: competenciaSchema,
});

export type ConsultaDoMes = z.infer<typeof consultaDoMesSchema>;

/* ---------------------------------------------------------------- importações */

/**
 * O corpo de `POST /financas/importacoes/folha`: as linhas marcadas na
 * conferência, cada uma com o nome do funcionário e o líquido.
 */
export const importacaoDaFolhaSchema = z.object({
  residenciaId: idSchema,
  competencia: competenciaSchema,
  data: dataSchema,
  linhas: z
    .array(
      z.object({
        favorecidoNome: textoObrigatorio('Funcionário', 200),
        valor: valorPositivoSchema,
      }),
    )
    .min(1, { message: 'Nenhum funcionário selecionado.' }),
});

export type ImportacaoDaFolha = z.infer<typeof importacaoDaFolhaSchema>;

/** Uma linha da guia DAE como a conferência a manda. */
export const linhaDaGuiaDaeSchema = z.object({
  codigo: z.string().regex(/^\d{4}$/, { message: 'Código inválido.' }),
  denominacao: z.string().trim().max(200),
  subcategoria: textoObrigatorio('Subcategoria', 120),
  valor: valorPositivoSchema,
});

export type LinhaDaGuiaDae = z.infer<typeof linhaDaGuiaDaeSchema>;

/**
 * O corpo de `POST /financas/importacoes/guia-dae`. A competência é o período de
 * apuração, nulo quando o PDF não o trazia.
 */
export const importacaoDaGuiaDaeSchema = z.object({
  residenciaId: idSchema,
  competencia: competenciaSchema.nullable(),
  data: dataSchema,
  itens: z.array(linhaDaGuiaDaeSchema).min(1, { message: 'Nenhum item selecionado.' }),
});

export type ImportacaoDaGuiaDae = z.infer<typeof importacaoDaGuiaDaeSchema>;

/** A resposta das rotas que criam lançamentos: quantos nasceram. */
export const resultadoDaImportacaoSchema = z.object({ criados: z.number().int() });

export type ResultadoDaImportacao = z.infer<typeof resultadoDaImportacaoSchema>;

/** O corpo de `PUT /financas/folhas-importadas`: a folha XLSX do mês. */
export const novaFolhaImportadaSchema = z.object({
  residenciaId: idSchema,
  competencia: competenciaSchema,
  itens: z
    .array(z.object({ nome: textoObrigatorio('Nome', 200), valor: valorPositivoSchema }))
    .min(1, { message: 'A folha não tem item.' }),
});

export type NovaFolhaImportada = z.infer<typeof novaFolhaImportadaSchema>;

/** A folha XLSX de um mês, com o total somado na resposta. */
export const folhaImportadaSchema = z.object({
  id: idSchema,
  competencia: competenciaSchema,
  itens: z.array(
    z.object({ posicao: z.number().int(), nome: z.string(), valor: valorGravadoSchema }),
  ),
  total: valorGravadoSchema,
});

export type FolhaImportada = z.infer<typeof folhaImportadaSchema>;

/* -------------------------------------------------------------- calculadora */

/** O recorte de `GET /financas/ajuda-custo/ultimo`. */
export const consultaDoUltimoGrupoSchema = z.object({ favorecidoId: idSchema });

/** Um cartão com diária, marcado: a diária com quatro casas e os dias inteiros. */
const cartaoComDiariaSchema = z.object({
  valorDiaria: valorPorUnidadeOpcionalSchema,
  dias: z
    .number()
    .int({ message: 'Os dias precisam ser um número inteiro e não negativo.' })
    .min(0, { message: 'Os dias precisam ser um número inteiro e não negativo.' }),
});

/** Um cartão de valor fixo, marcado. */
const cartaoFixoSchema = z.object({ valor: dinheiroSchema.nullable() });

/**
 * O corpo de `POST /financas/calculadora-petrella`, pela aba de onde vem.
 *
 * Em `ajuda_custo`, o cartão ausente é o desmarcado. Em `diarista`, os dias
 * aceitam fração, porque só entram no valor e no texto da descrição.
 */
export const calculadoraPetrellaSchema = z.discriminatedUnion('aba', [
  z.object({
    aba: z.literal('ajuda_custo'),
    residenciaId: idSchema,
    favorecidoNome: textoObrigatorio('Funcionário', 200),
    cpf: cpfDoGrupoSchema,
    pagador: textoOpcional(200),
    competencia: competenciaSchema,
    data: dataSchema,
    ferias: z.boolean(),
    custo: cartaoComDiariaSchema.optional(),
    diarias: cartaoComDiariaSchema.optional(),
    medica: cartaoFixoSchema.optional(),
    inss: cartaoFixoSchema.optional(),
  }),
  z.object({
    aba: z.literal('diarista'),
    residenciaId: idSchema,
    favorecidoNome: textoObrigatorio('Diarista', 200),
    competencia: competenciaSchema,
    data: dataSchema,
    valorDiaria: valorPorUnidadeOpcionalSchema,
    dias: z.number().min(0, { message: 'Os dias não podem ser negativos.' }),
  }),
]);

export type CalculadoraPetrella = z.infer<typeof calculadoraPetrellaSchema>;

/* -------------------------------------------------------------- recorrência */

/** O corpo de `POST /financas/ajuda-custo/recorrencia`. */
export const recorrenciaSchema = z.object({
  residenciaId: idSchema,
  forcar: z.boolean().default(false),
});

export type Recorrencia = z.infer<typeof recorrenciaSchema>;

/**
 * A resposta da recorrência. `executada` é falso quando alguém já verificou
 * hoje; `criados` conta grupos, e não lançamentos; `corrigidos` conta as
 * competências em que um grupo órfão foi apagado.
 */
export const resultadoDaRecorrenciaSchema = z.object({
  executada: z.boolean(),
  criados: z.number().int(),
  corrigidos: z.number().int(),
});

export type ResultadoDaRecorrencia = z.infer<typeof resultadoDaRecorrenciaSchema>;

/* ------------------------------------------------------------ outros bancos */

/** Os dois tipos do lançamento de outros bancos: "↗ Despesa" e "↙ Entrada". */
export const TIPO_LANCAMENTO_OUTROS_BANCOS = ['despesa', 'receita'] as const;

export const tipoLancamentoOutrosBancosSchema = z.enum(TIPO_LANCAMENTO_OUTROS_BANCOS, {
  message: 'Tipo desconhecido.',
});

export type TipoLancamentoOutrosBancos = z.infer<typeof tipoLancamentoOutrosBancosSchema>;

/** Uma linha da guia DAE gravada no cartão de Outros Bancos. */
export const linhaDaGuiaGravadaSchema = z.object({
  posicao: z.number().int(),
  codigo: z.string(),
  denominacao: z.string(),
  subcategoria: z.string(),
  valor: valorGravadoSchema,
});

/**
 * A situação do mês de um favorecido de conta apartada.
 *
 * Vem uma por favorecido, inclusive sem linha gravada. `dias` é o que está
 * gravado; `diasEfetivos` é o que o cartão mostra, por `diasEfetivos`, já
 * olhando o mês anterior e a folha fechada dele.
 */
export const situacaoDoMesSchema = z.object({
  favorecidoId: idSchema,
  salarioLiquido: valorGravadoSchema.nullable(),
  salarioImportadoEm: dataSchema.nullable(),
  dias: z.object({ custo: z.number().int().nullable(), diarias: z.number().int().nullable() }),
  diasEfetivos: z.object({ custo: z.number().int(), diarias: z.number().int() }),
  guiaDae: z
    .object({
      competencia: competenciaSchema.nullable(),
      importadaEm: dataSchema.nullable(),
      itens: z.array(linhaDaGuiaGravadaSchema),
      total: valorGravadoSchema,
    })
    .nullable(),
});

export type SituacaoDoMes = z.infer<typeof situacaoDoMesSchema>;

/**
 * Uma folha fechada: a fotografia do cartão no dia em que foi fechada.
 *
 * `favorecidoId` fica nulo quando o favorecido foi excluído; o nome e o título
 * continuam, e a folha continua no Fechamento.
 */
export const folhaFechadaSchema = z.object({
  id: idSchema,
  favorecidoId: idSchema.nullable(),
  favorecidoNome: z.string(),
  competencia: competenciaSchema,
  titulo: z.string(),
  fechadoEm: dataSchema,
  diasUsados: z.object({
    custo: z.number().int().nullable(),
    diarias: z.number().int().nullable(),
  }),
  itens: z.array(
    z.object({
      nome: z.string(),
      valor: valorGravadoSchema,
      subitens: z.array(z.object({ nome: z.string(), valor: valorGravadoSchema })),
    }),
  ),
  total: valorGravadoSchema,
});

export type FolhaFechada = z.infer<typeof folhaFechadaSchema>;

/**
 * Um lançamento de outros bancos como a API o devolve.
 *
 * `competencia` é o mês em que ele foi lançado, que pode não ser o da `data`.
 * O favorecido é texto, e não vínculo com o cadastro.
 */
export const lancamentoDeOutrosBancosSchema = z.object({
  id: idSchema,
  competencia: competenciaSchema,
  data: dataSchema,
  tipo: tipoLancamentoOutrosBancosSchema,
  categoria: z.string(),
  subcategoria: z.string().nullable(),
  favorecido: z.string().nullable(),
  formaPagamento: formaPagamentoLancamentoSchema,
  valor: valorGravadoSchema,
  descricao: z.string(),
});

export type LancamentoDeOutrosBancos = z.infer<typeof lancamentoDeOutrosBancosSchema>;

/**
 * A resposta de `GET /financas/outros-bancos`: a aba inteira de um mês e os
 * dois cartões do Fechamento que dependem dela.
 *
 * As folhas fechadas vêm na ordem em que foram fechadas pela primeira vez, e os
 * lançamentos pela data, como texto.
 */
export const outrosBancosDoMesSchema = z.object({
  competencia: competenciaSchema,
  situacoes: z.array(situacaoDoMesSchema),
  folhasFechadas: z.array(folhaFechadaSchema),
  lancamentos: z.array(lancamentoDeOutrosBancosSchema),
});

export type OutrosBancosDoMes = z.infer<typeof outrosBancosDoMesSchema>;

/** O favorecido e o mês das rotas `financas/outros-bancos/{favorecidoId}/{competencia}/…`. */
export const mesDoFavorecidoSchema = z.object({
  favorecidoId: idSchema,
  competencia: competenciaSchema,
});

export type MesDoFavorecido = z.infer<typeof mesDoFavorecidoSchema>;

/**
 * O corpo de `PUT …/salario`. Nulo apaga o salário; `importado` grava a data de
 * hoje na marca de importação, e o `false` não toca nela.
 */
export const salarioDeOutrosBancosSchema = z.object({
  salarioLiquido: dinheiroSchema.nullable(),
  importado: z.boolean(),
});

export type SalarioDeOutrosBancos = z.infer<typeof salarioDeOutrosBancosSchema>;

/** O corpo de `PUT …/dias`: os dois dias como estão à vista. */
export const diasDeOutrosBancosSchema = z.object({
  custo: diaDoMesSchema,
  diarias: diaDoMesSchema,
});

/** O corpo de `PUT …/guia-dae`: a guia que substitui a do mês. */
export const guiaDaeDeOutrosBancosSchema = z.object({
  competencia: competenciaSchema.nullable(),
  itens: z.array(linhaDaGuiaDaeSchema).min(1, { message: 'Nenhum item selecionado.' }),
});

export type GuiaDaeDeOutrosBancos = z.infer<typeof guiaDaeDeOutrosBancosSchema>;

/** O corpo de `POST /financas/outros-bancos/lancamentos`. */
export const novoLancamentoDeOutrosBancosSchema = z.object({
  residenciaId: idSchema,
  competencia: competenciaSchema,
  data: dataSchema,
  tipo: tipoLancamentoOutrosBancosSchema,
  categoria: textoObrigatorio('Categoria', 120),
  subcategoria: textoOpcional(120),
  favorecido: textoOpcional(200),
  formaPagamento: formaPagamentoLancamentoSchema,
  valor: valorPositivoSchema,
  descricao: textoObrigatorio('Descrição', 500),
});

export type NovoLancamentoDeOutrosBancos = z.infer<typeof novoLancamentoDeOutrosBancosSchema>;

/** O corpo de `POST /financas/outros-bancos/colaboradores`. */
export const colaboradorDeOutrosBancosSchema = z.object({ favorecidoId: idSchema });

/* ---------------------------------------------------------------- documentos */

/** Uma linha de recibo ou de ordem: a descrição e o valor. */
const linhaDeDocumentoSchema = z.object({ descricao: z.string(), valor: valorGravadoSchema });

/**
 * O recorte de `GET /financas/documentos/recibo`: um lançamento, ou o cartão de
 * Outros Bancos de um favorecido num mês.
 */
export const consultaDoReciboSchema = z.union([
  z.object({ lancamentoId: idSchema }),
  mesDoFavorecidoSchema,
]);

export type ConsultaDoRecibo = z.infer<typeof consultaDoReciboSchema>;

/**
 * O recibo pronto para desenhar. Os itens já vêm com o 1/3 de férias
 * destrinchado, e o total é o que o corpo do recibo escreve.
 */
export const reciboDePagamentoSchema = z.object({
  favorecido: z.string(),
  cpf: z.string().nullable(),
  pagador: z.string(),
  competencia: competenciaSchema,
  data: dataSchema,
  itens: z.array(linhaDeDocumentoSchema),
  total: valorGravadoSchema,
});

export type ReciboDePagamento = z.infer<typeof reciboDePagamentoSchema>;

/** A ordem de pagamento pronta para desenhar. */
export const ordemDePagamentoSchema = z.object({
  competencia: competenciaSchema,
  debitoDe: z.string().nullable(),
  favorecido: z.object({
    nome: z.string(),
    cpf: z.string().nullable(),
    banco: z.string().nullable(),
    agencia: z.string().nullable(),
    conta: z.string().nullable(),
    tipoConta: z.string().nullable(),
  }),
  itens: z.array(linhaDeDocumentoSchema),
  total: valorGravadoSchema,
  data: dataSchema,
});

export type OrdemDePagamento = z.infer<typeof ordemDePagamentoSchema>;
